import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronDown, Handshake, X, Menu } from "lucide-react";
import { scrollToHashTarget } from "@/lib/scrollToHash";
import logo from "@/assets/logo-toraflex.png";

interface NavbarProps {
  onOpenSidebar: () => void;
  alwaysOpaque?: boolean;
}

const qualidadeSubmenu = [
  { href: "/qualidade/certificacoes", label: "Certificações" },
  { href: "/qualidade/compliance", label: "Compliance & Integridade" },
];

export default function Navbar({ onOpenSidebar, alwaysOpaque }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [scrolled, setScrolled] = useState(alwaysOpaque ?? isMobile);
  const [activeSection, setActiveSection] = useState("");
  const [qualidadeOpen, setQualidadeOpen] = useState(false);
  const [mobileQualidadeOpen, setMobileQualidadeOpen] = useState(false);
  const qualidadeRef = useRef<HTMLDivElement>(null);

  const links = [
    { href: "/historia", label: "História" },
    { href: "/produtos", label: "Produtos" },
    { href: "/#depoimentos", label: "Depoimentos" },
    { href: "/#faq", label: "FAQ" },
    { href: "/trabalhe-conosco", label: "Trabalhe Conosco" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(Boolean(alwaysOpaque || window.innerWidth < 768 || window.scrollY > 20));

      const sectionIds = links.filter((link) => link.href.includes("#")).map((link) => link.href.split("#")[1]);
      let current = "";

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const { top } = el.getBoundingClientRect();
          if (top <= 140) current = id;
        }
      }

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [alwaysOpaque]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (qualidadeRef.current && !qualidadeRef.current.contains(e.target as Node)) {
        setQualidadeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, closeMobile = false) => {
    if (closeMobile) setMobileOpen(false);
    if (!href.includes("#") || window.location.pathname !== "/") return;

    const hash = `#${href.split("#")[1]}`;
    e.preventDefault();
    window.history.replaceState(null, "", `/`);
    scrollToHashTarget(hash);
  };

  const isQualidadePath = typeof window !== "undefined" && window.location.pathname.startsWith("/qualidade");

  return (
    <nav className={`fixed top-0 w-full z-50 py-4 transition-all duration-300 ${
      mobileOpen
        ? "bg-white border-b border-slate-200/60 shadow-sm"
        : scrolled
        ? "bg-white/80 backdrop-blur-md border-b border-slate-200/60 shadow-sm"
        : "bg-transparent border-b border-transparent shadow-none"
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <img src={logo} alt="Toraflex Colchões" className={`h-7 md:h-8 w-auto object-contain transition-all duration-300 group-hover:opacity-80 ${scrolled ? "" : "brightness-0 invert"}`} width={140} height={32} />
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {links.map((l) => {
              const id = l.href.includes("#") ? l.href.split("#")[1] : "";
              const isActive = Boolean(id) && activeSection === id;

              return (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={(e) => handleLinkClick(e, l.href)}
                  className={`text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 group relative ${isActive ? "text-brand-red" : scrolled ? "text-slate-600 hover:text-brand-red" : "text-white hover:text-brand-red"}`}
                >
                  <ChevronRight className={`w-2.5 h-2.5 text-brand-red transition-all duration-200 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                  {l.label}
                  <span className={`absolute -bottom-1 left-0 h-[2px] bg-brand-red rounded-full transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              );
            })}

            {/* Qualidade & Conformidade dropdown */}
            <div className="relative" ref={qualidadeRef}>
              <button
                type="button"
                onClick={() => setQualidadeOpen(!qualidadeOpen)}
                className={`text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 group relative ${isQualidadePath ? "text-brand-red" : scrolled ? "text-slate-600 hover:text-brand-red" : "text-white hover:text-brand-red"}`}
              >
                <ChevronRight className={`w-2.5 h-2.5 text-brand-red transition-all duration-200 ${isQualidadePath ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                Qualidade & Conformidade
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${qualidadeOpen ? "rotate-180" : ""}`} />
                <span className={`absolute -bottom-1 left-0 h-[2px] bg-brand-red rounded-full transition-all duration-300 ${isQualidadePath ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>

              {qualidadeOpen && (
                <div className="absolute top-full left-0 mt-3 bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[220px] z-50">
                  {qualidadeSubmenu.map((sub) => (
                    <Link
                      key={sub.href}
                      to={sub.href}
                      onClick={() => setQualidadeOpen(false)}
                      className="block px-4 py-2.5 text-sm text-slate-600 hover:text-brand-red hover:bg-slate-50 transition-colors"
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center">
            <a href="https://revenda.toraflex.com.br/" target="_blank" rel="noopener noreferrer" className="bg-brand-red hover:bg-brand-redHover text-white text-sm font-bold px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center gap-2">
              <Handshake className="w-4 h-4" />
              Seja um parceiro
            </a>
          </div>

          <button type="button" aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"} onClick={() => setMobileOpen(!mobileOpen)} className={`lg:hidden focus:outline-none transition-colors duration-300 min-w-[48px] min-h-[48px] flex items-center justify-center ${scrolled ? "text-slate-900" : "text-white"}`}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-slate-100 shadow-xl py-4 px-6 flex flex-col gap-3">
            {links.map((l) => {
              const id = l.href.includes("#") ? l.href.split("#")[1] : "";
              const isActive = Boolean(id) && activeSection === id;

              return (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={(e) => handleLinkClick(e, l.href, true)}
                  className={`block font-semibold py-3 min-h-[48px] flex items-center gap-2 transition-colors duration-200 ${isActive ? "text-brand-red" : "text-slate-700 hover:text-brand-red"}`}
                >
                  {isActive && <span className="w-1.5 h-1.5 rounded-full bg-brand-red" />}
                  {l.label}
                </Link>
              );
            })}

            {/* Mobile Qualidade submenu */}
            <button
              type="button"
              onClick={() => setMobileQualidadeOpen(!mobileQualidadeOpen)}
              className={`block font-semibold py-3 min-h-[48px] flex items-center gap-2 transition-colors duration-200 w-full text-left ${isQualidadePath ? "text-brand-red" : "text-slate-700 hover:text-brand-red"}`}
            >
              {isQualidadePath && <span className="w-1.5 h-1.5 rounded-full bg-brand-red" />}
              Qualidade & Conformidade
              <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${mobileQualidadeOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileQualidadeOpen && (
              <div className="pl-4 flex flex-col gap-1">
                {qualidadeSubmenu.map((sub) => (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    onClick={() => setMobileOpen(false)}
                    className="block font-medium py-2.5 min-h-[44px] flex items-center text-slate-600 hover:text-brand-red transition-colors text-sm"
                  >
                    <ChevronRight className="w-3 h-3 text-brand-red mr-2" />
                    {sub.label}
                  </Link>
                ))}
              </div>
            )}

            <hr className="border-slate-100" />
            <a href="https://revenda.toraflex.com.br/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="block w-full text-center bg-brand-red text-white font-bold py-3 min-h-[48px] rounded-lg flex items-center justify-center">
              Seja um parceiro
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
